import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { ArrowLeft, Heart, Check, HelpCircle, X, Trash2, Edit2, Camera } from 'lucide-react';
import { format } from 'date-fns';
import { API_URL } from '../utils/api';
import { toast } from 'sonner';

const VERDICT_CONFIG = {
  'GO': { icon: Check, bg: 'bg-qc-green', text: 'text-qc-green' },
  'MAYBE': { icon: HelpCircle, bg: 'bg-qc-amber', text: 'text-qc-amber' },
  'HELL NO': { icon: X, bg: 'bg-qc-red', text: 'text-qc-red' }
};

export default function Favorites() {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const response = await axios.get(`${API_URL}/favorites`, { withCredentials: true });
      setFavorites(response.data.favorites || []);
    } catch (e) {
      console.error('Failed to load favorites:', e);
      toast.error('Could not load favorites');
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (favoriteId, itemLabel) => {
    try {
      await axios.delete(`${API_URL}/favorites/${favoriteId}`, { withCredentials: true });
      setFavorites(prev => prev.filter(f => f.id !== favoriteId));
      toast.success(`Removed ${itemLabel} from favorites`);
    } catch (e) {
      toast.error('Could not remove favorite');
    }
  };

  const startEditNote = (fav) => {
    setEditingNote(fav.id);
    setNoteText(fav.note || '');
  };

  const saveNote = async (favoriteId) => {
    try {
      await axios.patch(
        `${API_URL}/favorites/${favoriteId}`,
        { note: noteText || null },
        { withCredentials: true }
      );
      setFavorites(prev => prev.map(f => 
        f.id === favoriteId ? { ...f, note: noteText || null } : f
      ));
      setEditingNote(null);
      toast.success('Note saved');
    } catch (e) {
      toast.error('Could not save note');
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return 'Unknown date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-qc-green border-t-transparent" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-qc-bg text-qc-text"
      style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      data-testid="favorites-page"
    >
      {/* Header */}
      <div className="px-5 py-3 flex items-center max-w-lg mx-auto">
        <button
          onClick={() => navigate('/home')}
          data-testid="favorites-back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-black text-qc-text ml-3 tracking-tight flex items-center gap-2">
          <Heart className="w-6 h-6 text-qc-red fill-qc-red" />
          Favorites
        </h1>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-2 pb-24">
        {favorites.length === 0 ? (
          /* Empty State */
          <div className="text-center py-16" data-testid="favorites-empty">
            <div className="w-20 h-20 bg-qc-surface rounded-full flex items-center justify-center mx-auto mb-4 border border-qc-border">
              <Heart className="w-8 h-8 text-qc-text-faint" />
            </div>
            <h2 className="text-xl font-bold text-qc-text mb-2">No favorites yet</h2>
            <p className="text-sm text-qc-text-dim mb-6 max-w-xs mx-auto">
              Tap the heart on any scan result to save products you want to track or repurchase.
            </p>
            <Button
              onClick={() => navigate('/scan')}
              data-testid="favorites-scan-cta"
              className="bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full px-6 h-12 font-extrabold"
            >
              <Camera className="w-5 h-5 mr-2" />
              Scan Something
            </Button>
          </div>
        ) : (
          /* Favorites List */
          <div className="space-y-3">
            <p className="text-sm text-qc-text-dim mb-4" data-testid="favorites-count">
              {favorites.length} saved {favorites.length === 1 ? 'item' : 'items'}
            </p>
            
            {favorites.map((fav) => {
              const config = VERDICT_CONFIG[fav.decision] || VERDICT_CONFIG['MAYBE'];
              const Icon = config.icon;
              
              return (
                <div
                  key={fav.id}
                  className="bg-qc-surface rounded-2xl border border-qc-border p-4"
                  data-testid={`favorite-item-${fav.id}`}
                >
                  <div className="flex gap-3">
                    {/* Thumbnail */}
                    {fav.thumbnail_url ? (
                      <div className="w-16 h-16 rounded-xl overflow-hidden bg-qc-surface2 flex-shrink-0">
                        <img 
                          src={fav.thumbnail_url} 
                          alt={fav.item_label || 'Favorited item'}
                          data-testid={`favorite-thumbnail-${fav.id}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-xl bg-qc-surface2 flex items-center justify-center flex-shrink-0">
                        <Heart className="w-6 h-6 text-qc-text-faint" />
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-bold text-qc-text text-sm truncate" data-testid={`favorite-label-${fav.id}`}>
                            {fav.item_label}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${config.bg} text-qc-bg`}
                              data-testid={`favorite-verdict-${fav.id}`}
                            >
                              <Icon className="w-3 h-3" strokeWidth={3} />
                              {fav.decision}
                            </span>
                            <span className="text-xs text-qc-text-faint" data-testid={`favorite-date-${fav.id}`}>
                              {formatDate(fav.added_at)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEditNote(fav)}
                            data-testid={`edit-note-${fav.id}`}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-qc-text-faint hover:text-qc-text hover:bg-qc-surface2"
                            title="Add note"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeFavorite(fav.id, fav.item_label)}
                            data-testid={`remove-favorite-${fav.id}`}
                            className="w-8 h-8 flex items-center justify-center rounded-full text-qc-text-faint hover:text-qc-red hover:bg-qc-red/10"
                            title="Remove from favorites"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      {/* Note */}
                      {editingNote === fav.id ? (
                        <div className="mt-2">
                          <input
                            type="text"
                            value={noteText}
                            onChange={(e) => setNoteText(e.target.value)}
                            data-testid={`favorite-note-input-${fav.id}`}
                            placeholder="Add a note (e.g., 'Buy at Costco')"
                            className="w-full h-9 px-3 bg-qc-surface2 border border-qc-border rounded-lg text-sm text-qc-text placeholder:text-qc-text-faint focus:outline-none focus:border-qc-green"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') saveNote(fav.id);
                              if (e.key === 'Escape') setEditingNote(null);
                            }}
                          />
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              onClick={() => saveNote(fav.id)}
                              data-testid={`favorite-note-save-${fav.id}`}
                              className="h-7 px-3 bg-qc-green text-qc-bg text-xs font-bold rounded-full"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingNote(null)}
                              data-testid={`favorite-note-cancel-${fav.id}`}
                              className="h-7 px-3 text-qc-text-dim text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : fav.note ? (
                        <p className="mt-2 text-xs text-qc-text-dim italic" data-testid={`favorite-note-${fav.id}`}>
                          &ldquo;{fav.note}&rdquo;
                        </p>
                      ) : null}
                      
                      {/* Reasons preview */}
                      {fav.reasons && fav.reasons.length > 0 && (
                        <p className="mt-2 text-xs text-qc-text-faint line-clamp-1">
                          {fav.reasons[0]}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
